
import React from 'react';
import { ChevronLeft, Zap, Heart, Users, Trophy, Star, Target, Sparkles, Rocket } from 'lucide-react';
import { AppView } from '../types.ts';
import { AURA_TITLES } from '../constants.tsx';

interface AuraGuideViewProps {
  setActiveView: (view: AppView) => void;
}

export default function AuraGuideView({ setActiveView }: AuraGuideViewProps) {
  
  const EARNING_METHODS = [
    { 
      name: 'Cooking Rots', 
      reward: '100 XP', 
      icon: <Rocket size={24}/>, 
      color: 'text-indigo-400', 
      desc: 'The main grind. Every generation gives base XP plus a rarity bonus!' 
    },
    { 
      name: 'Vibe Checking', 
      reward: '1 XP', 
      icon: <Heart size={24}/>, 
      color: 'text-red-500', 
      desc: 'Vote on other Sigmas. It adds up fast if you have enough rizz.' 
    },
    { 
      name: 'Social Rizz', 
      reward: '2-5 XP', 
      icon: <Users size={24}/>, 
      color: 'text-emerald-400', 
      desc: 'Gained when you follow others or they follow your frequency.' 
    },
  ];

  return (
    <div className="max-w-6xl mx-auto space-y-12 animate-in slide-in-from-bottom-8">
      {/* Header */}
      <div className="bg-zinc-950/60 backdrop-blur-xl border-2 border-indigo-500/20 rounded-[3rem] p-10 shadow-2xl space-y-4">
        <div className="flex items-center gap-4">
          <button onClick={() => setActiveView('editor')} className="p-3 bg-zinc-900 rounded-2xl hover:scale-110 active:scale-90 transition-transform shadow-lg">
            <ChevronLeft size={24}/>
          </button>
          <div className="flex flex-col">
            <h2 className="text-5xl font-bungee text-indigo-400 uppercase tracking-tighter leading-none">AURA TRANSMISSION</h2>
            <p className="text-[10px] font-black text-zinc-500 uppercase tracking-[0.3em] px-1">Climb the Sigma Ladder</p>
          </div>
        </div>
        <p className="text-sm font-bold text-zinc-300 max-w-2xl leading-relaxed">
          Aura is your status in the Brainrot Blast universe. It represents your frequency, your influence, and your raw power. Higher levels unlock new **Titles** and show everyone who the real Alpha is.
        </p>
      </div>

      {/* How to Earn */}
      <div className="space-y-6">
        <div className="flex items-center gap-3 border-l-4 border-indigo-500 pl-4 py-1">
          <Zap size={18} className="text-indigo-500" />
          <h3 className="font-bungee text-2xl uppercase tracking-widest">HOW TO GAIN AURA</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {EARNING_METHODS.map(method => (
            <div key={method.name} className="bg-zinc-900 border-2 border-white/5 p-8 rounded-[2.5rem] space-y-4 shadow-xl hover:translate-y-[-5px] transition-transform">
              <div className={`p-4 rounded-2xl bg-zinc-950 border border-white/5 w-fit ${method.color}`}>
                {method.icon}
              </div>
              <div className="space-y-1">
                <h4 className="text-2xl font-bungee uppercase text-white">{method.name}</h4>
                <div className={`font-bungee text-xs ${method.color}`}>{method.reward} BASE</div>
              </div>
              <p className="text-[10px] font-bold text-zinc-500 uppercase leading-relaxed tracking-wider">
                {method.desc}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Title Roadmap */}
      <div className="space-y-6">
        <div className="flex items-center gap-3 border-l-4 border-purple-500 pl-4 py-1">
          <Trophy size={18} className="text-purple-500" />
          <h3 className="font-bungee text-2xl uppercase tracking-widest">ROAD TO ALPHA</h3>
        </div>
        <div className="bg-zinc-950 border-4 border-zinc-900 rounded-[3rem] p-8 md:p-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            {AURA_TITLES.map((title, idx) => (
              <div key={title} className="bg-zinc-900/40 p-6 rounded-2xl border border-white/5 flex flex-col items-center text-center gap-3 group hover:border-indigo-500/50 transition-colors">
                <div className={`font-bungee text-sm ${idx === 0 ? 'text-zinc-600' : 'text-indigo-400'}`}>LVL {idx * 10}+</div>
                <div className="w-12 h-12 rounded-full bg-zinc-950 flex items-center justify-center text-zinc-500 group-hover:text-indigo-400 transition-colors">
                  {idx === 0 ? <CircleIcon size={16}/> : idx === 9 ? <CrownIcon size={16}/> : <TargetIcon size={16}/>}
                </div>
                <h5 className="font-bungee text-[10px] uppercase text-white leading-tight">{title}</h5>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Multipliers Box */}
      <div className="bg-gradient-to-r from-indigo-900 to-purple-900 p-10 rounded-[3rem] shadow-2xl relative overflow-hidden group">
        <div className="absolute inset-0 bg-white/5 opacity-0 group-hover:opacity-100 transition-opacity"></div>
        <div className="relative z-10 flex flex-col md:flex-row items-center gap-8">
          <div className="p-6 bg-indigo-500 rounded-3xl text-white shadow-lg animate-pulse">
            <Sparkles size={40} />
          </div>
          <div className="space-y-2 flex-1">
            <h3 className="font-bungee text-3xl text-white uppercase leading-none">AURA VELOCITY</h3>
            <p className="text-xs font-bold text-indigo-200 uppercase tracking-widest leading-relaxed">
              Upgrading your Rank in the **Sigma Store** adds a permanent XP multiplier to every single action! Registered users get +0%, but **God Tier** Sigmas get a massive **+75% Aura Boost**.
            </p>
          </div>
          <button 
            onClick={() => setActiveView('store')}
            className="px-10 py-5 bg-white text-indigo-950 font-bungee text-xl rounded-2xl hover:scale-105 active:scale-95 transition-all shadow-xl"
          >
            INCREASE VELOCITY
          </button>
        </div>
      </div>
    </div>
  );
}

const CircleIcon = ({ size }: { size: number }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /></svg>;
const TargetIcon = ({ size }: { size: number }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m12 14 4-4"/><path d="M3.34 19a10 10 0 1 1 17.32 0"/></svg>;
const CrownIcon = ({ size }: { size: number }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m2 4 3 12h14l3-12-6 7-4-7-4 7-6-7zm3 16h14"/></svg>;
